<template>
  <div>
    <Layout ref="layoutRef" :selectedOption="1" />

    <!-- Breadcrumb y ayuda -->
    <div class="mb-3">
      <div class="row align-items-center">
        <div class="col-11">
          <div class="breadcrumb-container">
            <router-link to="/interconsulta">Interconsultas</router-link>
            &rsaquo;
            <router-link :to="`/interconsultadestinoform/${p_idAtencion}/${p_idInterconsulta}`">
              Paciente: {{ datoInterconsulta.paciente }}
            </router-link>
            &rsaquo; Informe
          </div>
        </div>
        <div class="col-1 text-end">
          <button class="btn-help" type="button" @click="showModalHelp = true" title="Abrir ayuda">
            <span class="material-symbols-outlined">help</span>
          </button>
        </div>
      </div>
    </div>

    <hr />

    <!-- Bloque colapsable: Información de atención -->
    <div class="d-flex justify-content-between align-items-center mb-2">
      <b>Información de la atención médica</b>
      <button class="btn btn-link p-0 text-decoration-none" @click="mostrarBloqueAtencion = !mostrarBloqueAtencion">
        <span class="material-symbols-outlined align-middle">
          {{ mostrarBloqueAtencion ? 'expand_less' : 'expand_more' }}
        </span>
        {{ mostrarBloqueAtencion ? 'Mostrar menos' : 'Mostrar más información' }}
      </button>
    </div>
    <div v-if="mostrarBloqueAtencion" class="form-card mb-3">
      <div class="mb-3 row">
        <label class="col-1 col-form-label">Médico</label>
        <div class="col-5">
          <input class="form-control" v-model="datoInterconsulta.medicoAtencion" disabled />
        </div>
        <label class="col-1 col-form-label">Estado</label>
        <div class="col-5">
          <input class="form-control" v-model="datoInterconsulta.estadoInter" disabled />
        </div>
      </div>
      <div class="mb-3 row">
        <label class="col-1 col-form-label">Motivo</label>
        <div class="col-11">
          <textarea class="form-control" v-model="datoInterconsulta.motivo" disabled />
        </div>
      </div>
      <div class="mb-3 row">
        <label class="col-1 col-form-label">Cuadro</label>
        <div class="col-11">
          <textarea class="form-control" v-model="datoInterconsulta.cuadro" disabled />
        </div>
      </div>
    </div>

    <hr />

    <!-- Tabs -->
    <h5>Interconsulta</h5>
    <div class="tab-bar">
      <button
        class="tablink"
        :class="{ 'active-tab': showTable === 'interconsulta' }"
        @click="showTable = 'interconsulta'"
      >
        <span class="material-symbols-outlined">assignment</span>
        Información general
      </button>
      <button
        class="tablink"
        :class="{ 'active-tab': showTable === 'diagInter' }"
        @click="showTable = 'diagInter'"
      >
        <span class="material-symbols-outlined">diagnosis</span>
        Diagnósticos
      </button>
      <button
        class="tablink"
        :class="{ 'active-tab': showTable === 'MedInter' }"
        @click="showTable = 'MedInter'"
      >
        <span class="material-symbols-outlined">medication</span>
        Medicamentos
      </button>
    </div>

    <!-- Tab: Información general -->
    <div v-if="showTable === 'interconsulta'" class="form-card mt-3">
      <div class="row mb-3">
        <div v-for="field in dictationFields" :key="field.key" class="col-6 mb-3">
          <label class="form-label">{{ field.label }}</label>
          <div class="input-group">
            <textarea
              class="form-control"
              v-model="datoInformeInter[field.key]"
              style="text-transform: uppercase"
              maxlength="250"
            />
            <button
              class="btn btn-outline-secondary mic-btn"
              type="button"
              :disabled="isAnyDictating && !isDictating(field.key)"
              @click="toggleDictation(field.key)"
              :title="isDictating(field.key) ? 'Detener dictado' : 'Iniciar dictado'"
            >
              <span
                class="material-symbols-outlined"
                :class="isDictating(field.key) ? 'mic-active' : 'mic-inactive'"
              >
                {{ isDictating(field.key) ? 'mic' : 'mic_off' }}
              </span>
            </button>
          </div>
        </div>
      </div>
      <div class="row mb-3">
        <div class="col-4">
          <label class="form-label">Fecha Registro</label>
          <input class="form-control" :value="getFormattedDate(datoInformeInter.fechaReg)" disabled />
        </div>
        <div class="col-4">
          <label class="form-label">Id Informe</label>
          <input class="form-control" v-model="datoInformeInter.idInformeInter" disabled />
        </div>
        <div class="col-4">
          <label class="form-label">Id Interconsulta</label>
          <input class="form-control" v-model="datoInformeInter.idInterconsulta" disabled />
        </div>
      </div>
      <div class="d-flex gap-2 justify-content-end mt-2">
        <button class="btn btn-success" @click.prevent="guardarEditInformeInter">
          <span class="material-symbols-outlined align-middle" style="font-size: 20px;">update</span>
          Actualizar
        </button>
        <button class="btn btn-warning" @click.prevent="actualizarEstado('FINALIZADA')">
          <span class="material-symbols-outlined align-middle" style="font-size: 20px;">task_alt</span>
          Finalizar Interconsulta
        </button>
      </div>
    </div>

    <!-- Tab: Diagnósticos -->
    <div v-else-if="showTable === 'diagInter'">
      <DiagInter :p_idInformeInter="datoInformeInter.idInformeInter" />
    </div>

    <!-- Tab: Medicamentos -->
    <div v-else-if="showTable === 'MedInter'">
      <MedInter :p_idInformeInter="datoInformeInter.idInformeInter" />
    </div>

    <Footer />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Layout from '@/components/Layout.vue'
import Footer from '@/components/Footer.vue'
import DiagInter from '../forms/DiagInter.vue'
import MedInter from '../forms/MedInter.vue'
import { validarForm as validarFormModule } from '@/modules/validacionesForms.js'
import dateFormat from '@/modules/dateFormat.js'
import SpeechDictation from '@/modules/microfono.js'
import api from '@/services/api'
import swal from 'sweetalert'

const route = useRoute()
const router = useRouter()
const layoutRef = ref(null)

// ── Parámetros de ruta ──────────────────────────────────────────────────────
const p_idAtencion      = ref(route.params.p_idatencion)
const p_idInterconsulta = ref(route.params.p_idinterconsulta)

// ── Estado UI ───────────────────────────────────────────────────────────────
const showModalHelp         = ref(false)
const mostrarBloqueAtencion = ref(false)
const showTable             = ref('interconsulta')

// ── Datos ───────────────────────────────────────────────────────────────────
const datoInterconsulta = ref({})
const datoInformeInter  = ref({})

// ── Dictado ─────────────────────────────────────────────────────────────────
const dictation            = new SpeechDictation()
const activeDictationField = ref(null)

const dictationFields = [
  { key: 'cuadro',   label: 'Cuadro' },
  { key: 'resumen',  label: 'Resumen' },
  { key: 'planDiag', label: 'Plan Diagnóstico' },
  { key: 'planTera', label: 'Plan Terapéutico' },
]

const isDictating    = (field) => activeDictationField.value === field
const isAnyDictating = computed(() => activeDictationField.value !== null)

function toggleDictation(field) {
  if (isDictating(field)) {
    dictation.stopDictation()
    activeDictationField.value = null
  } else {
    dictation.startDictation((transcript) => {
      datoInformeInter.value[field] = transcript
    }, datoInformeInter.value[field])
    activeDictationField.value = field
  }
}

// ── Helpers ─────────────────────────────────────────────────────────────────
function getFormattedDate(date) {
  return dateFormat.getFormattedDate(date)
}

// ── API ──────────────────────────────────────────────────────────────────────
function getData() {
  api.get(`/vwinterconsultas/destino/${p_idInterconsulta.value}`)
    .then(res => { datoInterconsulta.value = res.data })
    .catch(err => console.error('Error al obtener datos de la interconsulta:', err))
}

async function abrirEditar() {
  try {
    const resp = await api.get(`/vwinformeinters/idinterconsulta/${p_idInterconsulta.value}`)
    datoInformeInter.value = resp.data
  } catch (err) {
    console.error('Error al obtener los datos del informe:', err)
  }
}

function validarFormLocal() {
  const fields = ['idInterconsulta', 'cuadro', 'resumen', 'planDiag', 'planTera']
  return validarFormModule(datoInformeInter.value, fields, fields)
}

async function guardarEditInformeInter() {
  if (validarFormLocal() !== 1) return

  let guardado = false
  try {
    await api.post('/informeinters', datoInformeInter.value)
    guardado = true
    swal('Correcto!', 'Informe creado exitosamente!', 'success')
  } catch (error) {
    swal('Error!', 'Inténtelo de nuevo!', 'error')
    Object.values(error.response?.data?.errors ?? {}).forEach(e => swal(String(e)))
  }

  if (!guardado) return

  try {
    await api.put(`/interconsultas/actualizar-estado/${p_idInterconsulta.value}/PROCESO`)
  } catch (error) {
    swal('Error!', 'No se pudo actualizar el estado.', 'error')
    Object.values(error.response?.data?.errors ?? {}).forEach(e => swal(String(e)))
  }
}

async function actualizarEstado(estado) {
  if (validarFormLocal() !== 1) return
  try {
    await api.put(`/interconsultas/actualizar-estado/${p_idInterconsulta.value}/${estado}`)
    swal('Correcto!', 'Informe actualizado!', 'success')
    router.push('/interconsulta')
  } catch (error) {
    swal('Error!', 'Inténtelo de nuevo!', 'error')
    Object.values(error.response?.data?.errors ?? {}).forEach(e => swal(String(e)))
  }
}

// ── Ciclo de vida ────────────────────────────────────────────────────────────
onMounted(async () => {
  getData()
  layoutRef.value?.toggleProcesos()
  layoutRef.value?.toggleReportes()
  layoutRef.value?.toggleParametros()
  layoutRef.value?.toggleAyuda()
  await abrirEditar()
})
</script>

<style scoped>
/* Botón de ayuda */
.btn-help {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #0d6efd;
}
.btn-help .material-symbols-outlined {
  font-size: 32px;
  transition: color 0.2s;
}
.btn-help:hover .material-symbols-outlined {
  color: #0a58ca;
}

/* Tab bar */
.tab-bar {
  display: flex;
  align-items: stretch;
  gap: 4px;
  border-bottom: 2px solid #dee2e6;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
  padding: 6px 8px 0;
  flex-wrap: wrap;
}
.tablink {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 7px 12px 8px;
  border: 1px solid transparent;
  border-bottom: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  color: #6c757d;
  border-radius: 6px 6px 0 0;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;
}
.tablink:hover {
  background: #e9ecef;
  color: #343a40;
}
.active-tab {
  background: #fff;
  color: #0d6efd;
  border-color: #dee2e6;
  font-weight: 500;
  margin-bottom: -2px;
  border-bottom: 2px solid #fff;
}

/* Micrófono */
.mic-active {
  font-size: 36px;
  color: #198754;
  cursor: pointer;
}
.mic-inactive {
  font-size: 36px;
  color: orangered;
  cursor: pointer;
}
.mic-btn:disabled {
  opacity: 0.4;
}
.input-group .dropdown-menu {
  width: 100%;
}
.dropdown-item {
  cursor: pointer;
}
</style>

<style src="@/assets/css/microfono.css" scoped></style>